import { CommonModule } from '@angular/common';
import { Component, NgModule, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterModule } from '@angular/router';
import { applyLanguageFilteringMulti } from '@app/common/filtering-behavior';
import { Roles } from '@app/common/roles';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { WPNavigationNode } from '@app/models/navigation-node';
import { UserLanguageTerm } from '@app/models/user-language-term';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { NavigationNodeService } from '@app/services/navigation-node.service';
import { SecurityService } from '@app/services/security.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { SortablejsModule } from 'ngx-sortablejs';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { first, map, mergeMap, switchMap } from 'rxjs/operators';
import { Options } from 'sortablejs';
import { LanguageTermService } from '@app/services/language-term.service';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { LanguageTermNavigationNode } from '@app/models/language-term-navigation-node';

@Component({
  selector: 'app-find-information-light-menu',
  templateUrl: './find-information-light-menu.component.html',
  styleUrls: ['./find-information-light-menu.component.scss']
})
export class FindInformationLightMenuComponent implements OnInit, OnDestroy {

  navigationNodes$: Observable<WPNavigationNode[]>;
  hasPermissionEditorOrAdmin$: Observable<boolean>;
  navigationNodes: WPNavigationNode[];
  editMode = false;
  isLoading = false; // used to avoid skippy rendering when switching to edit mode.
  updateNodeOrderSubscription: Subscription;
  options: Options;
  languages: Array<SimplifiedTerm>;
  getLanguagesSubscription: Subscription;
  languageFilteringEnabled = this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace;

  constructor(
    private navigationNodeService: NavigationNodeService,
    private securityService: SecurityService,
    private userChannelTermsService: UserChannelTermsService,
    private featureSettingsStoreService: FeatureSettingsStoreService,
    private languageTermService: LanguageTermService,
    private userLanguageTermService: UserLanguageTermService) {

    this.options = {
      disabled: false,
      handle: '.drag-handle',
      filter: '.ignore-elements',
      onMove: (event: any) => {
        // to ignore the top level add button in the sorting.
        return !event.related.classList.contains('ignore-elements');
      },
      onUpdate: () => {
        this.onSortUpdate();
      }

    };
  }

  private onSortUpdate() {
    const movedNodes = new Array<WPNavigationNode>();
    this.navigationNodes.forEach((node, i) => {
      if (node.Order !== i) {
        node.Order = i;
        movedNodes.push(node);
      }
      node.Children.forEach((subNode, j) => {
        if (subNode.Order !== j) {
          subNode.Order = j;
          movedNodes.push(subNode);
        }
        subNode.Children.forEach((subsubNode, y) => {
          if (subsubNode.Order !== y) {
            subsubNode.Order = y;
            movedNodes.push(subsubNode);
          }
        });
      });

    });

    this.updateNodeOrderSubscription = this.navigationNodeService.updateNavigationNodeOrder(movedNodes).subscribe(
      {
        error: () => {
          console.log('ordering update failed');
          this.navigationNodeService.reload();
        }
      });
  }

  getOrder(parent: WPNavigationNode = null) {
    return parent && parent.Children ? parent.Children.length : this.navigationNodes.length;
  }

  ngOnInit(): void {
    const navigationNodes$ = this.navigationNodeService
      .refresh$
      .pipe(
        mergeMap(() => {
          return forkJoin([
            this.navigationNodeService.getAllNavigationNodes(),
            this.userChannelTermsService.getUserChannelTerms()
          ]);
        }),
        map(([nodes, userChannelTerms]) => {

          // don't filter in edit mode
          if (this.editMode) {
            return nodes;
          }

          // filter nodes based on userChannels
          return nodes.filter(n => {

            let nodeHasTerms = (n.ChannelTermNavigationNodes && n.ChannelTermNavigationNodes.length > 0);
            let matchesUserTerms = false;

            if (nodeHasTerms) {
              matchesUserTerms = n.ChannelTermNavigationNodes.some(c => userChannelTerms.some(uct => uct.ChannelTermId === c.ChannelTermId));
            }
            if (nodeHasTerms && !matchesUserTerms) {
              // The node isn't targeted to the user, hence it _and_ it's children shouldn't be further processed
              return false;
            }
            n.Children = n.Children.filter(nc => {

              // filter children
              nc = (nc as WPNavigationNode);
              nodeHasTerms = (nc.ChannelTermNavigationNodes && nc.ChannelTermNavigationNodes.length > 0);
              matchesUserTerms = false;
              if (nodeHasTerms) {
                matchesUserTerms = nc.ChannelTermNavigationNodes.some(c => userChannelTerms.some(uct => uct.ChannelTermId === c.ChannelTermId));
              }
              if (nodeHasTerms && !matchesUserTerms) {
                // The node isn't targeted to the user, hence it _and_ it's children shouldn't be further processed
                return false;
              }
              nc.Children = nc.Children.filter(ngc => {
                // filter grandchildren
                nodeHasTerms = (ngc.ChannelTermNavigationNodes && ngc.ChannelTermNavigationNodes.length > 0);
                if (nodeHasTerms) {
                  return ngc.ChannelTermNavigationNodes.some(c => userChannelTerms.some(uct => uct.ChannelTermId === c.ChannelTermId));
                }
                // node doesn't have terms
                return true;
              });
              // node doesn't have terms OR has terms matching the user
              return true;
            });
            // node doesn't have terms OR has terms matching the user
            return true;
          });
        })
      );

    this.navigationNodes$ = navigationNodes$
      .pipe(
        switchMap((navigationNodes) => {
          // Language filter toggle
          if (this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace && !this.editMode) {
            return this.userLanguageTermService
              .getUserLanguageTerms()
              .pipe(
                map((userLanguageTerms) => {
                  // Make sure to apply lanugage filtering through all of the levels, so call a recursive wrapper method around applyLanguageFilteringMulti
                  return this.languageFilterNavigationNodes(navigationNodes, 'LanguageTermNavigationNodes', userLanguageTerms);
                }));
          } else {
            return of(navigationNodes);
          }
        }),
        map(data => {
          // sort top level nodes
          return data.sort((a: WPNavigationNode, b: WPNavigationNode) => {
            return a.Order - b.Order;
          });
        }),
        map(data => {
          // sort second and third level nodes
          data.forEach((node => {
            node.Children.sort((a: WPNavigationNode, b: WPNavigationNode) => {
              return a.Order - b.Order;
            });
            node.Children.forEach(subNode => {
              subNode.Children = subNode.Children.sort((a: WPNavigationNode, b: WPNavigationNode) => {
                return a.Order - b.Order;
              });
            });
          }));
          this.navigationNodes = data;
          this.isLoading = false;
          return data;
        }));

    this.hasPermissionEditorOrAdmin$ = this.securityService.hasUserAnyAppRole([Roles.Admin, Roles.Editor]);

    if (this.languageFilteringEnabled) {
      this.getLanguagesSubscription = this.languageTermService.getLanguages().pipe(first()).subscribe(l => { this.languages = l; });
    }
  }

  languageFilterNavigationNodes(elements: WPNavigationNode[], propertyName: string, userLanguages: UserLanguageTerm[]) {
    const filteredNodes = applyLanguageFilteringMulti(elements, propertyName, userLanguages, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
    filteredNodes.forEach(node => {
      if (node?.Children?.length > 0) {
        node.Children = this.languageFilterNavigationNodes(node.Children, propertyName, userLanguages);
      }
    });
    return filteredNodes;
  }

  ngOnDestroy(): void {
    this.updateNodeOrderSubscription && this.updateNodeOrderSubscription.unsubscribe();
    this.getLanguagesSubscription && this.getLanguagesSubscription.unsubscribe();
  }

  handleEditMode(change: MatSlideToggleChange) {
    this.isLoading = true;
    this.editMode = change.checked;
    this.navigationNodeService.useCache = !this.editMode; // disable the service cache in edit mode.
    this.navigationNodeService.reload();
  }

  getNodeLanguages(nodeLanguages: LanguageTermNavigationNode[]): string {
    if (!this.languages || this.languages.length === 0 || !nodeLanguages || nodeLanguages.length === 0) { return ''; }
    const result = new Array<string>();
    nodeLanguages.forEach(nodeLanguage => {
    const matchingLanguage = this.languages.find(l => l.Guid.toLowerCase() === nodeLanguage.LanguageTermId.toLowerCase());
    if (matchingLanguage && matchingLanguage.Name) {
        result.push(matchingLanguage.Name);
      }
    });
    result.sort();
    return '  ' + result.join(', ');
  }
}

@NgModule({
  declarations: [FindInformationLightMenuComponent],
  exports: [
    FindInformationLightMenuComponent
  ],
  imports: [
    CommonModule,
    WidgetPlaceholderModule,
    LinkModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    SortablejsModule,
    RouterModule,
  ]
})
export class FindInformationLightMenuModule { }
